/**
 * Client Service
 * Provides client search and creation functionality using API
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

/**
 * React Query hook returning the broker's clients (cached across modal opens).
 * Paginates through `/clients` 100 at a time and stops when a page returns
 * fewer than 100 — so brokers with more than 100 clients still get the full
 * list. A hard MAX_PAGES ceiling prevents a runaway loop if the backend ever
 * ignores the `skip` parameter.
 */
export function useClients(brokerId = null) {
  return useQuery({
    queryKey: ['clients', 'list', brokerId || 'all'],
    queryFn: async () => {
      const filters = brokerId ? { broker_id: brokerId } : {};
      const PAGE_SIZE = 100;
      const MAX_PAGES = 50; // safety cap = 5000 clients
      const all = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const data = await apiClient.getClients(page * PAGE_SIZE, PAGE_SIZE, filters);
        const arr = Array.isArray(data) ? data : (data?.data || []);
        if (arr.length === 0) break;
        all.push(...arr);
        if (arr.length < PAGE_SIZE) break;
      }
      return all;
    },
    staleTime: 5 * 60 * 1000,    // 5 min — opens within this window hit cache
    gcTime: 30 * 60 * 1000,      // keep in memory for 30 min after last use
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Fetch all clients
 * @param {number} skip - Number of items to skip (pagination)
 * @param {number} limit - Maximum number of items to return
 * @param {string} brokerId - Broker ID to filter clients by
 * @returns {Promise<Array>} - Array of all clients
 */
export async function fetchClients(skip = 0, limit = 100, brokerId = null) {
  try {
    const filters = brokerId ? { broker_id: brokerId } : {};
    const clients = await apiClient.getClients(skip, limit, filters);
    return clients;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
}

/**
 * Search clients by query string
 * @param {string} query - Search query (matches against name, company, or email)
 * @param {string} brokerId - Broker ID to filter clients by
 * @returns {Promise<Array>} - Array of matching clients
 */
export async function searchClients(query, brokerId = null) {
  try {
    if (!query || typeof query !== 'string') {
      return await fetchClients(0, 100, brokerId);
    }

    const normalizedQuery = query.toLowerCase().trim();

    // Fetch all clients and filter on the frontend
    // If the API supports search parameters, you can pass them as filters
    const filters = { search: normalizedQuery };
    if (brokerId) {
      filters.broker_id = brokerId;
    }
    const clients = await apiClient.getClients(0, 100, filters);

    // Fallback: Filter on frontend if API doesn't support search
    // Remove this if API handles search server-side
    const results = clients.filter(client => {
      return (
        client.first_name?.toLowerCase().includes(normalizedQuery) ||
        client.last_name?.toLowerCase().includes(normalizedQuery) ||
        client.company?.toLowerCase().includes(normalizedQuery) ||
        client.email?.toLowerCase().includes(normalizedQuery)
      );
    });

    return results;
  } catch (error) {
    console.error('Error searching clients:', error);
    throw error;
  }
}

/**
 * Create a new client
 * @param {Object} clientData - Client data
 * @param {string} clientData.firstName - Client first name
 * @param {string} clientData.lastName - Client last name
 * @param {string} clientData.company - Company name
 * @param {string} clientData.email - Email address
 * @param {string} clientData.phone - Phone number
 * @returns {Promise<Object>} - Created client with generated ID
 */
export async function createClient(clientData) {
  try {
    // Validate required fields
    if (!clientData.firstName || !clientData.email) {
      throw new Error('First name and email are required');
    }

    // Call API to create client
    const newClient = await apiClient.post('/clients', {
      first_name: clientData.firstName.trim(),
      last_name: (clientData.lastName || '').trim(),
      company_name: clientData.company || '',
      email: clientData.email,
      broker_id: clientData.broker_id || '',
      contact_numbers: clientData.phone ? [clientData.phone] : [],
      address1: (clientData.address1 || '').trim(),
      city: (clientData.city || '').trim(),
      state: (clientData.state || '').trim(),
      zip_code: (clientData.zip || '').trim(),
    });

    return newClient;
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
}

/**
 * Get client by ID
 * @param {string} id - Client ID
 * @returns {Promise<Object|null>} - Client object or null if not found
 */
export async function getClientById(id) {
  try {
    const client = await apiClient.getClientById(id);
    return client;
  } catch (error) {
    console.error('Error fetching client by ID:', error);
    // Return null if client not found (404)
    if (error.status === 404) {
      return null;
    }
    throw error;
  }
}
